// Phase 63: shared filter state for the entire app.
// Mounted once in App.jsx so the header FilterBar (visible on every page)
// drives period/range/category filters consistently across Dashboard,
// Transactions, CardDetail, etc.
//
// State shape (matches Dashboard's existing useState):
//   period:    'YYYY-MM-DD' | 'YYYY-MM' | 'YYYY'
//   range:     'day' | 'month' | 'year'
//   catFilter: null | <local_uuid or numeric id>
//
// Setters:
//   setRangeAndResetPeriod(r) — matches Dashboard's existing helper so the
//     period key stays normalized to the active range shape.
//
// Implementation note: This file is .js (not .jsx) so it can be imported
// directly by Node tests without a JSX loader. We use React.createElement
// instead of JSX syntax.

import { createContext, createElement, useContext, useState, useCallback, useEffect, useMemo } from 'react';

// Phase 66b: localStorage key for compact mode (instant load on mount).
// Dexie write happens in the background via writes.setSetting for cross-device sync.
const COMPACT_KEY = 'finance_compact_filter';
const COMPACT_SETTING = 'compact_filter_enabled';

function readLocalCompact() {
  try {
    const v = window.localStorage.getItem(COMPACT_KEY);
    return v === 'true';
  } catch {
    return false;
  }
}
function writeLocalCompact(on) {
  try { window.localStorage.setItem(COMPACT_KEY, on ? 'true' : 'false'); } catch { /* ignore */ }
}

// Phase 49b: period key shape depends on range.
// Phase 70: '5y' (last 5 years rolling window) uses 'YYYY' shape like 'year'.
//   - range === 'day'   → 'YYYY-MM-DD'
//   - range === 'month' → 'YYYY-MM'
//   - range === 'year'  → 'YYYY'
//   - range === '5y'    → 'YYYY' (end year of the rolling 5-year window)
export function periodForRange(d, r) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  if (r === 'day')   return `${y}-${m}-${day}`;
  if (r === 'year')  return `${y}`;
  if (r === '5y')    return `${y}`; // end-year of the rolling 5y window
  return `${y}-${m}`; // 'month' (default)
}

const FilterContext = createContext(null);

export function FilterProvider({ children, initialRange = 'month' }) {
  const [period, setPeriod] = useState(periodForRange(new Date(), initialRange));
  const [range, setRange] = useState(initialRange);
  const [catFilter, setCatFilter] = useState(null);
  // Phase 66: compact mode hides category chip row (smaller header).
  // Phase 66b: persists across reloads via localStorage (instant) + Dexie (cross-device).
  const [compact, setCompactState] = useState(readLocalCompact);

  const setCompact = useCallback((on) => {
    setCompactState(on);
    writeLocalCompact(on);
    // Phase 66b: also persist to Dexie for cross-device sync (Phase 35e will replicate).
    // Dynamic import keeps filterContext importable from Node tests.
    import('./dataService.js').then(({ writes }) => {
      if (writes && writes.setSetting) {
        writes.setSetting(COMPACT_SETTING, on ? 'true' : 'false').catch(() => { /* offline OK */ });
      }
    }).catch(() => { /* offline OK */ });
  }, []);

  const setRangeAndResetPeriod = useCallback((r) => {
    setRange(r);
    setPeriod(periodForRange(new Date(), r));
  }, []);

  const clearCatFilter = useCallback(() => setCatFilter(null), []);

  const value = useMemo(() => ({
    period, setPeriod,
    range,  setRange,
    catFilter, setCatFilter,
    compact, setCompact,
    setRangeAndResetPeriod,
    clearCatFilter,
  }), [period, range, catFilter, compact, setRangeAndResetPeriod, clearCatFilter]);

  // Phase 66b: sync from Dexie on mount. localStorage is instant; Dexie is canonical.
  // If Dexie has a different value (e.g. user toggled on another device), update localStorage + state.
  useEffect(() => {
    import('./dataService.js').then(({ data }) => {
      if (!data || !data.getSetting) return;
      data.getSetting(COMPACT_SETTING).then((val) => {
        const fromDb = val === 'true';
        if (fromDb !== compact) {
          setCompactState(fromDb);
          writeLocalCompact(fromDb);
        }
      }).catch(() => { /* offline OK */ });
    }).catch(() => { /* dataService not available */ });
    // Only re-run if `compact` changes — no need to refire on every mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createElement(FilterContext.Provider, { value }, children);
}

export function useFilter() {
  const ctx = useContext(FilterContext);
  if (!ctx) {
    throw new Error('useFilter must be used inside <FilterProvider>');
  }
  return ctx;
}
